/* ==========================================================================
   fx-combo-option.js  —  splits one Shopify option into two sets of controls

   WHY THIS EXISTS
   Shopify allows a maximum of three options per product. Fox-Rings rings
   already use Size, Colour and Box, so "Customization" cannot be a fourth.
   Instead, Box and Customization are stored in Shopify as ONE option whose
   values pair them up:

       "1 custom side / Ring Only"
       "1 custom side / With Box"
       "2 custom sides / Ring Only"    ... and so on

   This element reads that single option out of Dawn's real variant picker,
   splits every value on the delimiter, and paints two independent rows of
   buttons. Clicking either row re-selects the underlying radio for the
   combined value, then lets Dawn's own variant-selects handle price, media,
   availability and the form. No variant logic is reimplemented here.

   If anything is missing — the option, the element, JavaScript itself — the
   real picker is simply left visible and the product still sells.
   ========================================================================== */
(function () {
  'use strict';

  function textOf(el) {
    return (el.getAttribute('value') || '').trim();
  }

  class FxComboOption extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.scope =
        this.closest('.shopify-section') || this.closest('.product') || document;
      this.optionName = (this.dataset.optionName || '').trim().toLowerCase();
      this.delimiter = this.dataset.delimiter || '/';
      if (this.build()) {
        this._ready = true;
        this.scope.addEventListener('change', this.sync.bind(this));
      }
    }

    /* Every radio belonging to the combined option, in Shopify's own order. */
    findRadios() {
      var all = Array.prototype.slice.call(
        this.scope.querySelectorAll('input[type="radio"][data-option-name]')
      );
      var mine = all.filter(function (r) {
        return (
          (r.getAttribute('data-option-name') || '').trim().toLowerCase() ===
          this.optionName
        );
      }, this);
      // No name given, or a typo in it: fall back to the one option whose
      // values all carry the delimiter. Beats failing silently.
      if (!mine.length) {
        var groups = {};
        all.forEach(function (r) {
          var n = (r.getAttribute('data-option-name') || '').trim();
          (groups[n] = groups[n] || []).push(r);
        });
        Object.keys(groups).forEach(function (n) {
          var g = groups[n];
          var every = g.every(function (r) {
            return textOf(r).indexOf(this.delimiter) > -1;
          }, this);
          if (every && g.length > mine.length) mine = g;
        }, this);
      }
      return mine;
    }

    build() {
      var radios = this.findRadios();
      if (radios.length < 2) return false;

      var delim = this.delimiter;
      var parsed = radios
        .map(function (r) {
          var raw = textOf(r);
          var i = raw.indexOf(delim);
          if (i < 0) return null;
          return {
            radio: r,
            raw: raw,
            a: raw.slice(0, i).trim(),
            b: raw.slice(i + delim.length).trim(),
            available: !r.classList.contains('disabled')
          };
        })
        .filter(Boolean);

      if (parsed.length !== radios.length) return false; // not a combined option

      this.parsed = parsed;
      this.valuesA = [];
      this.valuesB = [];
      parsed.forEach(function (p) {
        if (this.valuesA.indexOf(p.a) < 0) this.valuesA.push(p.a);
        if (this.valuesB.indexOf(p.b) < 0) this.valuesB.push(p.b);
      }, this);
      if (this.valuesA.length < 2 && this.valuesB.length < 2) return false;

      // hide the real picker only now that we know we can replace it
      var host = radios[0].closest('fieldset') || radios[0].parentNode;
      if (host) {
        host.hidden = true;
        host.setAttribute('aria-hidden', 'true');
        this._host = host;
      }

      this.innerHTML = '';
      this.rowA = this.makeRow(this.dataset.labelA || 'Customization', this.valuesA, 'a');
      this.rowB = this.makeRow(this.dataset.labelB || 'Box', this.valuesB, 'b');
      if (this.dataset.note) {
        var note = document.createElement('p');
        note.className = 'fx-combo__note';
        note.textContent = this.dataset.note;
        this.appendChild(note);
      }
      this.sync();
      return true;
    }

    makeRow(label, values, which) {
      if (values.length < 2) return null;
      var wrap = document.createElement('div');
      wrap.className = 'fx-combo__row';

      var lg = document.createElement('p');
      lg.className = 'fx-combo__label';
      lg.textContent = label;
      wrap.appendChild(lg);

      var list = document.createElement('div');
      list.className = 'fx-combo__list';
      values.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'fx-combo__btn';
        b.dataset.which = which;
        b.dataset.value = v;
        b.textContent = v;
        b.addEventListener('click', this.pick.bind(this, which, v));
        list.appendChild(b);
      }, this);
      wrap.appendChild(list);
      this.appendChild(wrap);
      return wrap;
    }

    current() {
      var checked = this.parsed.filter(function (p) {
        return p.radio.checked;
      })[0];
      return checked || this.parsed[0];
    }

    /* Choose a new value for one half, keeping the other half if that pair
       exists. If it does not, fall back to the first available pair. */
    pick(which, value) {
      var cur = this.current();
      var other = which === 'a' ? cur.b : cur.a;

      var exact = this.parsed.filter(function (p) {
        return p[which] === value && (which === 'a' ? p.b : p.a) === other;
      })[0];

      // Keep the shopper's other choice even when that exact pairing is sold
      // out — Dawn then shows it as unavailable, which is what Shopify does.
      // Only when the pairing does not exist at all do we move the other half.
      var target =
        exact ||
        this.parsed.filter(function (p) {
          return p[which] === value && p.available;
        })[0] ||
        this.parsed.filter(function (p) {
          return p[which] === value;
        })[0];

      if (!target || target.radio.checked) return;
      target.radio.checked = true;
      target.radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Repaint from whatever Dawn currently has selected. */
    sync() {
      if (!this.parsed) return;
      var cur = this.current();
      var self = this;

      this.querySelectorAll('.fx-combo__btn').forEach(function (btn) {
        var which = btn.dataset.which;
        var value = btn.dataset.value;
        var on = cur[which] === value;
        btn.classList.toggle('is-on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');

        // Grey out a choice that is sold out alongside the shopper's current
        // other choice — the same signal Dawn gives on a normal option.
        var other = which === 'a' ? cur.b : cur.a;
        var pair = self.parsed.filter(function (p) {
          return p[which] === value && (which === 'a' ? p.b : p.a) === other;
        })[0];
        var usable = pair
          ? pair.available
          : self.parsed.some(function (p) {
              return p[which] === value && p.available;
            });
        btn.classList.toggle('is-unavailable', !usable);
      });
    }
  }

  if (!customElements.get('fx-combo-option')) {
    customElements.define('fx-combo-option', FxComboOption);
  }
})();
